import React from 'react';
import styled from 'styled-components';

import { PermissionString } from '../../constants/permissions';
import { useHasPermission, useHasViewPermission, useUserGroup } from '../../hooks';
import { useGroupsQuery } from '../../queries';
import { UserService } from '../../services';
import { CUSTOMER, INTERNAL } from '../settings/AccessPolicies/constants';

interface Props {
  children: any;
}

const isDebugMode = () => process.env.NODE_ENV === 'development' && window.PERMISSIONS_DEBUG;

const Wrapper = styled.div<{ permission: PermissionString | PermissionString[] | string }>`
  position: relative;
  outline: 2px dashed green;

  &::after {
    content: ${(props) =>
      typeof props.permission === 'string' ? `"${props.permission}"` : `"${props.permission.join(', ')}"`};
    position: absolute;
    top: -0.5em;
    left: 1em;
    border-radius: 2px;
    padding: 0.25em;
    font-size: 12px;
    background: green;
    color: white;
    pointer-events: none;
  }
`;

export const isInternal = async (): Promise<boolean> => {
  const response = await UserService.isInternal();
  return response;
};

export function InternalOnly(props: Props) {
  const { data: groupsFromQuery } = useGroupsQuery();
  const groups = groupsFromQuery || [];

  const internalRoles = groups.filter((group) => group.scope === INTERNAL);
  const userGroupName = useUserGroup();

  const hasAccess = internalRoles.some((group) => group.name === userGroupName);

  if (hasAccess && isDebugMode()) {
    return <Wrapper permission="Internal only">{props.children}</Wrapper>;
  }

  if (hasAccess) {
    return props.children;
  }

  return null;
}

export function CustomerOnly(props: Props) {
  const { data: groupsFromQuery } = useGroupsQuery();
  const groups = groupsFromQuery || [];
  const customerRoles = groups.filter((group) => group.scope === CUSTOMER);

  const userGroupName = useUserGroup();
  const hasAccess = customerRoles.some((group) => group.name === userGroupName);

  if (hasAccess && isDebugMode()) {
    return <Wrapper permission="Customer only">{props.children}</Wrapper>;
  }

  if (hasAccess) {
    return props.children;
  }
  return null;
}

export function AdminOnly(props: Props) {
  const userGroup = useUserGroup();
  const hasAccess = userGroup === 'admin';

  if (hasAccess && isDebugMode()) {
    return <Wrapper permission="Admin only">{props.children}</Wrapper>;
  }

  if (hasAccess) {
    return props.children;
  }
  return null;
}

interface SinglePermissionProps {
  children: any;
  permission: PermissionString;
}

interface MultiplePermissionProps {
  children: any;
  permission: PermissionString[];
  all?: boolean;
}

type PermissionProps = SinglePermissionProps | MultiplePermissionProps;

export function Permission(props: PermissionProps) {
  const hasAccess = useHasViewPermission(props.permission);
  const hasAccessToAllPermissions = useHasPermission(props.permission);

  if (isDebugMode()) {
    // Handle case when we require ALL of the permissions
    if (props.all) {
      if (hasAccessToAllPermissions) {
        return <Wrapper permission={props.permission}>{props.children}</Wrapper>;
      }
      return null;
    }

    // Default case when we require ANY of the permissions
    if (hasAccess) {
      return <Wrapper permission={props.permission}>{props.children}</Wrapper>;
    }
    return null;
  }

  // Handle case when we require ALL of the permissions
  if (props.all) {
    if (hasAccessToAllPermissions) {
      return props.children;
    }
    return null;
  }

  // Default case when we require ANY of the permissions
  if (hasAccess) {
    return props.children;
  }
  return null;
}
